import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { BrowserQRCodeReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";

// Leitor de QR Code compartilhado (Verificação QR, Serigrafia, Empolvado).
//
// A leitura abre em um MODAL EM TELA CHEIA: a câmera ocupa a tela toda, o que
// entrega muito mais pixels do QR ao decodificador e facilita a mira no tablet.
//
// Estratégia de leitura (rápida e omnidirecional):
// 1. Se o navegador tiver a API nativa BarcodeDetector (Chrome Android/macOS,
//    usa o ML Kit do próprio sistema), usamos ela — acelerada por hardware,
//    lê em qualquer rotação. Quando o frame normal falha, tentamos também o
//    frame ESPELHADO (caso do QR lido pelo verso do vidro).
// 2. Senão, caímos no ZXing (v0.23) com TRY_HARDER — o decodificador de QR do
//    ZXing já tenta a variante espelhada sozinho.
const hints = new Map();
hints.set(DecodeHintType.TRY_HARDER, true);
hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);

// Intervalo entre tentativas de decodificação (ms). Menor = mais rápido.
const INTERVALO_NATIVO = 60;
const INTERVALO_ZXING = 60;

const criarDetectorNativo = async () => {
  if (!("BarcodeDetector" in window)) return null;
  try {
    const formatos = await window.BarcodeDetector.getSupportedFormats();
    if (!formatos.includes("qr_code")) return null;
    return new window.BarcodeDetector({ formats: ["qr_code"] });
  } catch {
    return null;
  }
};

const mensagemErroCamera = (err) => {
  switch (err?.name) {
    case "NotAllowedError":
      return "Permissão de câmera bloqueada. Toque no cadeado na barra de endereço do Chrome e permita a câmera.";
    case "NotFoundError":
      return "Nenhuma câmera encontrada neste dispositivo.";
    case "NotReadableError":
      return "A câmera está em uso por outro aplicativo. Feche-o e tente de novo.";
    case "SecurityError":
      return "O navegador bloqueou a câmera: a página precisa estar em HTTPS.";
    default:
      return "Não foi possível iniciar a câmera.";
  }
};

export default function QRCodeReaderOrdem({ onLeitura }) {
  const [cameraAtiva, setCameraAtiva] = useState(false);
  const [erroCamera, setErroCamera] = useState("");
  const videoRef = useRef(null);
  const lendoRef = useRef(false); // evita múltiplas leituras seguidas

  // Mantém a referência mais recente do callback sem reiniciar a câmera a cada
  // render do componente pai (onLeitura costuma ser recriada a cada render).
  const onLeituraRef = useRef(onLeitura);
  useEffect(() => {
    onLeituraRef.current = onLeitura;
  }, [onLeitura]);

  useEffect(() => {
    if (!cameraAtiva) return;

    let parado = false;
    let stream = null;
    let controlsZxing = null;
    let timer = null;
    const video = videoRef.current;

    const entregarLeitura = (texto) => {
      const valor = (texto || "").trim();
      if (!valor || lendoRef.current) return;
      lendoRef.current = true;
      onLeituraRef.current?.(valor);
      setTimeout(() => {
        setCameraAtiva(false);
        lendoRef.current = false;
      }, 300);
    };

    (async () => {
      setErroCamera("");

      // 1. Abre a câmera traseira em resolução alta (ajuda QR pequeno/inclinado)
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
      } catch (err) {
        console.error("Erro ao abrir a câmera:", err);
        setErroCamera(mensagemErroCamera(err));
        setCameraAtiva(false);
        return;
      }
      if (parado || !video) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      video.srcObject = stream;
      await video.play().catch(() => {});

      // 2. Foco contínuo, quando o hardware suporta (essencial para leitura rápida)
      try {
        const track = stream.getVideoTracks()[0];
        const caps = track.getCapabilities?.();
        if (caps?.focusMode?.includes("continuous")) {
          await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
        }
      } catch {
        /* foco contínuo indisponível — segue com o padrão */
      }

      // 3. Caminho nativo (BarcodeDetector) — rápido e omnidirecional
      const detector = await criarDetectorNativo();
      if (detector) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });

        const varrer = async () => {
          if (parado || lendoRef.current) return;
          if (video.readyState >= 2) {
            try {
              let codigos = await detector.detect(video);
              if (!codigos.length && ctx) {
                // Tenta o frame espelhado (QR visto pelo verso do vidro)
                const w = video.videoWidth;
                const h = video.videoHeight;
                if (w && h) {
                  canvas.width = w;
                  canvas.height = h;
                  ctx.save();
                  ctx.scale(-1, 1);
                  ctx.drawImage(video, -w, 0, w, h);
                  ctx.restore();
                  codigos = await detector.detect(canvas);
                }
              }
              if (codigos.length) {
                entregarLeitura(codigos[0].rawValue);
                return;
              }
            } catch (e) {
              console.debug("BarcodeDetector falhou no frame:", e);
            }
          }
          timer = setTimeout(varrer, INTERVALO_NATIVO);
        };
        varrer();
        return;
      }

      // 4. Fallback: ZXing (TRY_HARDER lê em qualquer direção; o decoder de QR
      //    já tenta a variante espelhada internamente)
      try {
        const codeReader = new BrowserQRCodeReader(hints, {
          delayBetweenScanAttempts: INTERVALO_ZXING,
        });
        controlsZxing = await codeReader.decodeFromStream(stream, video, (result) => {
          if (parado || !result) return; // erros por frame são normais
          const texto =
            typeof result.getText === "function" ? result.getText() : String(result);
          entregarLeitura(texto);
        });
      } catch (err) {
        console.error("Erro ao iniciar o leitor ZXing:", err);
        setErroCamera("Não foi possível iniciar o leitor de QR.");
        setCameraAtiva(false);
      }
    })();

    return () => {
      parado = true;
      clearTimeout(timer);
      try {
        controlsZxing?.stop();
      } catch (e) {
        /* noop */
      }
      stream?.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    };
  }, [cameraAtiva]);

  return (
    <div className="mt-3">
      <button
        type="button"
        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center gap-2"
        onClick={() => setCameraAtiva(true)}
      >
        <span role="img" aria-label="camera" style={{ fontSize: "1.2em" }}>
          📷
        </span>
        Ler QR Code
      </button>

      {erroCamera && (
        <div className="alert alert-warning mt-2 mb-0 py-2 px-3" role="alert">
          {erroCamera}
        </div>
      )}

      <Modal
        show={cameraAtiva}
        onHide={() => setCameraAtiva(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold fs-4">Aponte para o QR Code</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "min(60vh, 480px)",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#000",
            }}
          >
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Moldura de mira central */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "65%",
                height: "80%",
                maxWidth: "320px",
                transform: "translate(-50%, -50%)",
                boxSizing: "border-box",
                border: "3px dashed rgba(0, 255, 0, 0.7)",
                borderRadius: "16px",
                pointerEvents: "none",
              }}
            />
          </div>

          <p className="text-muted fs-5 text-center mt-3 mb-0">
            A leitura é automática — funciona em qualquer direção
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
}
